/**
 * The function will make an asynchronious rest call. If property identifier is undefined, it's mean that we try to
 * save a new template, and we will make a rest post call. But if identifier has a value, we suppose that is an
 * existing template, and we will make a rest.put call.
 */
import { LINKS_KEY, PAGES_KEY, PROPERTY_NAME, PROPERTY_VALUE, TYPE_KEY } from "../common/constant";
import { beginLoading, endLoading } from "./loadingSpinner";
import { createBackendErrorMessages, createErrorMessages, createSuccessMessages, createWarningMessages, messagesReceived } from "./message";
import { refreshTriggerAction, resetSurveyView, SURVEY_RESULT } from "./surveyView";

import { SurveyModel as $survey } from "../common/entities";
import navigate from "../common/navigate";
import rest from "../common/rest";
import PDFCreator from "../component/user/PDFCreator";
import ObjectUtils, { coalesce } from "../utils/ObjectUtils";
import { showDialog } from "./confirmDialog";
import { USER } from "./login";
import { hasSurveyDuplicatePageKeys, hasSurveyDuplicateQuestionKeys } from "./surveyEditor";
import { resetPages } from "./surveyEditorActions";
import { loadHistorySuccessful, loadTemplateListFilter, templateUpdated } from "./templatelist";

const { getDeepProperty } = ObjectUtils;

export const CREATE_NEW_ACTION = "SURVEY.CREATE_NEW_ACTION";
export const RESET_SURVEY_ACTION = "SURVEY.RESET_SURVEY_ACTION";
export const LOAD_SUCCESSFUL_ACTION = "SURVEY.LOAD_SUCCESSFUL_ACTION";
export const LOAD_COMPARISON_SUCCESSFUL_ACTION = "SURVEY.LOAD_COMPARISON_SUCCESSFUL_ACTION";
const LOAD_SUCCESSFUL_AND_CREATE_PDF_ACTION = "SURVEY.LOAD_SUCCESSFUL_AND_CREATE_PDF_ACTION";
const LOAD_SUCCESSFUL_AND_CREATE_PDF_BUFFER = "SURVEY.LOAD_SUCCESSFUL_AND_CREATE_PDF_BUFFER";
const PROPERTY_CHANGED_ACTION = "SURVEY.PROPERTY_CHANGED_ACTION";
const PREVIEW_SURVEY_SHOW_HIDE_ACTION = "SURVEY.PREVIEW_SURVEY_SHOW_HIDE_ACTION";
const QUESTION_SYNC_SHOW_HIDE_ACTION = "SURVEY.QUESTION_SYNC_SHOW_HIDE_ACTION";
const EDIT_HEADER_CONFIRMED_ACTION = "EDIT_HEADER_CONFIRMED_ACTION";
const CHANGE_EDIT_STATE_ACTION = "CHANGE_EDIT_STATE_ACTION";

export const LOADED = "loaded";
export const SURVEY_NAME_DRAFT = "surveyNameDraft";
export const SURVEY_DESCRIPTION_DRAFT = "surveyDescriptionDraft";
export const EDIT_SURVEY_HEADER = "editSurveyHeader";
export const QUESTION_SYNC = "questionSync";
export const SURVEY_IDENTIFIER = "identifier";
export const SURVEY_VERSION = "version";
export const SURVEY_NAME = "surveyName";
export const SURVEY_DESCRIPTION = "surveyDescription";
export const SURVEY_TEMPLATE = "surveyTemplate";
export const SURVEY_FUNDING_AREA = "surveyFundingArea";
export const SURVEY_PROGRAM = "surveyProgram";
export const SURVEY_PROGRAM_INSTANCE = "surveyProgramInstance";
export const SURVEY_PROJECT = "surveyProject";
export const SURVEY_LANGUAGE = "surveyLanguage";
export const SURVEY_RESET_PAGE = "surveyResetPage";
export const SHOW_SURVEY_PREVIEW = "showSurveyPreview";
export const SYNC_DIALOG_OPEN = "syncDialogOpen";
export const LAST_MODIFIED_DATE = "lastModifiedDate";
export const SURVEY_STATUS = "status";
export const SURVEY_BUFFER = "surveyBuffer";
export const SURVEY_COMPARE = "surveyCompare";
export const SURVEYS_COMPARE = "surveysCompare";
export const SURVEY_COMPARE_LATEST = "surveyCompareLatest";
export const SURVEY_COMPARE_OLD = "surveyCompareOld";
export const SURVEY_ACTIVATION_PERIOD = "surveyActivationPeriod";
export const SURVEYS_COMPARE_PATH = "/userArea/compare";
const PDF_CALL_BACK = "pdfCallBack";
const EMPLOYEE_NUMBER = "employeeNumber";

const initialState = {
  [SURVEY_IDENTIFIER]: null,
  [SURVEY_VERSION]: null,
  [SURVEY_NAME]: "",
  [SURVEY_DESCRIPTION]: "",
  [SURVEY_FUNDING_AREA]: null,
  [SURVEY_PROGRAM]: null,
  [SURVEY_PROGRAM_INSTANCE]: null,
  [SURVEY_PROJECT]: null,
  [SURVEY_LANGUAGE]: {},
  [SURVEY_TEMPLATE]: { pages: [] },
  [SHOW_SURVEY_PREVIEW]: false,
  [SYNC_DIALOG_OPEN]: false,
  [SURVEY_NAME_DRAFT]: null,
  [SURVEY_DESCRIPTION_DRAFT]: null,
  [EDIT_SURVEY_HEADER]: false,
  [SURVEY_BUFFER]: false,
  [SURVEY_COMPARE_LATEST]: null,
  [SURVEY_COMPARE_OLD]: null,
  [SURVEYS_COMPARE]: []
};

export default function reducer(state = initialState, action) {
  let newState;
  switch (action[TYPE_KEY]) {
    case LOAD_SUCCESSFUL_ACTION:
      newState = Object.assign({}, state, {
        [SURVEY_IDENTIFIER]: action[SURVEY_IDENTIFIER],
        [SURVEY_VERSION]: action[SURVEY_VERSION],
        [SURVEY_NAME]: action[SURVEY_NAME],
        [QUESTION_SYNC]: action[QUESTION_SYNC],
        [SURVEY_FUNDING_AREA]: action[SURVEY_FUNDING_AREA],
        [SURVEY_PROGRAM]: action[SURVEY_PROGRAM],
        [SURVEY_PROGRAM_INSTANCE]: action[SURVEY_PROGRAM_INSTANCE],
        [SURVEY_PROJECT]: action[SURVEY_PROJECT],
        [SURVEY_LANGUAGE]: action[SURVEY_LANGUAGE],
        [SURVEY_TEMPLATE]: action[SURVEY_TEMPLATE],
        [SURVEY_DESCRIPTION]: action[SURVEY_DESCRIPTION],
        [SHOW_SURVEY_PREVIEW]: action[SHOW_SURVEY_PREVIEW],
        [EDIT_SURVEY_HEADER]: false
      });
      break;
    case LOAD_SUCCESSFUL_AND_CREATE_PDF_ACTION:
      PDFCreator.createPDFForGivenTemplate(
        action[SURVEY_TEMPLATE],
        action[SURVEY_NAME],
        action[SURVEY_FUNDING_AREA],
        action[SURVEY_PROGRAM],
        action[SURVEY_LANGUAGE],
        action[SURVEY_RESULT],
        action[SURVEY_BUFFER],
        action[PDF_CALL_BACK],
        action[USER],
        undefined,
        action[EMPLOYEE_NUMBER]
      );
      newState = Object.assign({}, state, {});
      break;
    case LOAD_SUCCESSFUL_AND_CREATE_PDF_BUFFER:
      PDFCreator.createPDFForGivenTemplate(
        action[SURVEY_TEMPLATE],
        action[SURVEY_NAME],
        action[SURVEY_FUNDING_AREA],
        action[SURVEY_PROGRAM],
        action[SURVEY_LANGUAGE],
        action[SURVEY_RESULT],
        action[SURVEY_BUFFER],
        action[PDF_CALL_BACK],
        action[USER],
        undefined,
        action[EMPLOYEE_NUMBER]
      );
      newState = Object.assign({}, state, {});
      break;
    case CREATE_NEW_ACTION:
      navigate.to("/userArea/build", { state: true });
      newState = Object.assign({}, state, action);
      break;
    case RESET_SURVEY_ACTION:
      newState = Object.assign({}, state, initialState);
      break;
    case PROPERTY_CHANGED_ACTION:
      newState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: action[PROPERTY_VALUE]
      });
      break;
    case PREVIEW_SURVEY_SHOW_HIDE_ACTION:
      newState = Object.assign({}, state, {
        [SHOW_SURVEY_PREVIEW]: action[SHOW_SURVEY_PREVIEW]
      });
      break;
    case QUESTION_SYNC_SHOW_HIDE_ACTION:
      newState = Object.assign({}, state, {
        [SYNC_DIALOG_OPEN]: action[SYNC_DIALOG_OPEN]
      });
      break;
    case CHANGE_EDIT_STATE_ACTION:
      newState = Object.assign({}, state, {
        [EDIT_SURVEY_HEADER]: action[EDIT_SURVEY_HEADER],
        [SURVEY_NAME_DRAFT]: state[SURVEY_NAME],
        [SURVEY_DESCRIPTION_DRAFT]: state[SURVEY_DESCRIPTION]
      });
      break;
    case EDIT_HEADER_CONFIRMED_ACTION:
      newState = Object.assign({}, state, {
        [SURVEY_NAME]: state[SURVEY_NAME_DRAFT],
        [SURVEY_DESCRIPTION]: state[SURVEY_DESCRIPTION_DRAFT],
        [EDIT_SURVEY_HEADER]: false
      });
      break;
    case LOAD_COMPARISON_SUCCESSFUL_ACTION:
      newState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: action[PROPERTY_VALUE]
      });
      break;
    default:
      newState = Object.assign({}, state, {});
      break;
  }
  return newState;
}

export function propertyChange(propertyName, propertyValue) {
  return {
    [TYPE_KEY]: PROPERTY_CHANGED_ACTION,
    [PROPERTY_NAME]: propertyName,
    [PROPERTY_VALUE]: propertyValue
  };
}

export function saveSurvey(identifier, surveyName, link, sync) {
  const survey = {
    identifier,
    surveyName
  };
  return (dispatch, getState) => {
    dispatch(beginLoading());
    survey.surveyTemplate = {
      reportTemplateYear: getState().survey[SURVEY_TEMPLATE]?.reportTemplateYear,
      [PAGES_KEY]: getState().surveyEditor[PAGES_KEY]
    };
    if (getDeepProperty(getState().survey, SURVEY_FUNDING_AREA, "id")) {
      survey.fundingArea = getState().survey[SURVEY_FUNDING_AREA];
      survey.program = getState().survey[SURVEY_PROGRAM];
      survey.programInstance = getState().survey[SURVEY_PROGRAM_INSTANCE];
      survey.project = getState().survey[SURVEY_PROJECT];
    }
    survey.language = getState().survey[SURVEY_LANGUAGE];
    survey[$survey.description] = getState().survey[SURVEY_DESCRIPTION];
    link = coalesce(link, `/admin/admin/surveys`);
    //if identifier is undefined, we suppose it is a new survey, otherwise we believe it is an existing template.
    (!identifier
      ? rest.doPost(`${link}`, JSON.stringify(survey))
      : rest.doPut(`${link}/${identifier}`, JSON.stringify(survey))
    ).then(
      (json) => {
        if (hasSurveyDuplicateQuestionKeys(survey.surveyTemplate) || hasSurveyDuplicatePageKeys(survey.surveyTemplate)) {
          dispatch(messagesReceived(createWarningMessages("survey.editor.message.template_saved.duplicate.keys.message")));
        } else {
          dispatch(messagesReceived(createSuccessMessages("survey.editor.message.template_saved")));
        }
        dispatch(saveSuccessful(json, json.links));

        // call question library sync if we want to sync as well
        if (sync) {
          dispatch(syncQuestions(json));
        }

        dispatch(endLoading());
      },
      (reason) => {
        dispatch(messagesReceived(createErrorMessages(reason.message.code)));
        dispatch(endLoading());
      }
    );
  };
}

export function saveAndSyncSurveyQuestions(identifier, surveyName, link) {
  return (dispatch) => {
    dispatch(saveSurvey(identifier, surveyName, link, true));
  };
}

/**
 * The function will make an asynchronious rest call. If property identifier is undefined, it's mean that we try to
 * save a new template, and we will make a rest post call. But if identifier has a value, we suppose that is an
 * existing template, and we will make a rest.put call.
 */
export function saveSurveySettings(identifier, surveyName, link) {
  const survey = {
    identifier,
    surveyName
  };
  return (dispatch, getState) => {
    dispatch(beginLoading());
    survey.fundingArea = getDeepProperty(getState(), "fundingArea", "selectedFundingArea", "settings");
    survey.program = getDeepProperty(getState(), "fundingArea", "selectedProgram", "settings");
    survey.programInstance = getDeepProperty(getState(), "fundingArea", "selectedProgramInstance", "settings");
    survey.language = getDeepProperty(getState(), "surveySettings", SURVEY_LANGUAGE);
    link = coalesce(link, `/admin/admin/surveys`);
    //if identifier is undefined, we suppose it is a new survey, otherwise we belive it is an existing template.
    rest.doPut(`${link}/${identifier}/settings`, JSON.stringify(survey)).then(
      (json) => {
        if (json.identifier) {
          loadTemplateListFilter(survey.fundingArea, survey.program)(dispatch, getState);
          dispatch(saveSuccessful(json, json.links));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

/**
 * Function try to load the survey from the server, and if everything is ok, redirecting to the editor pager
 */
export function loadSurvey(survey, push = true, createPDF = false) {
  let identifier = coalesce(
    ObjectUtils.getDeepProperty(survey, "identifier"),
    ObjectUtils.getDeepProperty(survey, "surveyId")
  );
  return (dispatch, getState) => {
    dispatch(beginLoading());
    resetSurvey();
    rest
      .doGet(
        //survey._links.self.href
        `/admin/admin/surveys/${identifier}`
      )
      .then(
        (json) => {
          if (json.identifier) {
            push && navigate.to("/userArea/build", { state: true });
            dispatch(loadSuccessful(json, json.links));
            createPDF && dispatch(loadSuccessfulAndCreatePDF(json));
            refreshTriggerAction()(dispatch, getState);
            dispatch(endLoading());
          } else {
            dispatch(messagesReceived(createErrorMessages(json)));
            dispatch(endLoading());
          }
        },
        (reason) => {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
          dispatch(endLoading());
        }
      );
  };
}

/**
 * Function try to load the survey from the server, and if everything is ok, redirecting to the editor pager
 */
export function loadSurveyVersion(
  survey,
  historyVersion,
  push = true,
  createPDF = false,
  showPreview = false,
  buffer = false,
  pdfCallBack
) {
  let identifier = coalesce(
    ObjectUtils.getDeepProperty(survey, "identifier"),
    ObjectUtils.getDeepProperty(survey, "surveyId")
  );
  let version = historyVersion.version;
  return (dispatch, getState) => {
    dispatch(beginLoading());
    resetSurvey();
    rest
      .doGet(
        //survey._links.self.href
        `/admin/admin/surveys/${identifier}/version/${version}`
      )
      .then(
        (json) => {
          if (json.identifier) {
            push && navigate.to("/userArea/build", { state: true });
            createPDF && dispatch(loadSuccessfulAndCreatePDF(json));
            buffer && dispatch(loadSuccessfulCreateBuffer(json, pdfCallBack));
            refreshTriggerAction()(dispatch, getState);
            dispatch(loadSuccessful(json, json.links, showPreview));
            dispatch(endLoading());
          } else {
            dispatch(messagesReceived(createErrorMessages(json)));
            dispatch(endLoading());
          }
        },
        (reason) => {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
          dispatch(endLoading());
        }
      );
  };
}

export function surveyVersionCompare(template) {
  let identifier = coalesce(
    ObjectUtils.getDeepProperty(template, "identifier"),
    ObjectUtils.getDeepProperty(template, "surveyId")
  );
  return (dispatch) => {
    dispatch(beginLoading());
    rest.doGet(`/admin/admin/surveys/history/${identifier}`).then(
      (json) => {
        if (json.historyList) {
          const latestVersion = template.version;
          let latestActivatedVersion = template.version;

          // get the latest activated version
          json.historyList.every((hist) => {
            if (hist.status === "active") {
              latestActivatedVersion = hist.version;
              return false;
            }
            return true;
          });

          if ((latestVersion === latestActivatedVersion || latestActivatedVersion === 0) && latestVersion > 1) {
            dispatch(endLoading());
            dispatch(
              showDialog(
                {},
                () => {
                  dispatch(beginLoading());
                  dispatch(getVersionForComparison(identifier, latestVersion, latestVersion - 1));
                },
                "survey.editor.message.version.compare.warning.title",
                "survey.editor.message.version.compare.warning.message",
                "confirmDialog.ok",
                "confirmDialog.cancel",
              )
            );
          } else {
            dispatch(getVersionForComparison(identifier, latestVersion, latestActivatedVersion));
          }

        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

export function getVersionsForComparison(identifier, lastVersion) {
  return (dispatch) => {
    rest.doGet(`/admin/admin/surveys/history/${identifier}/active`).then(
      (jsonResponse) => {
        if (jsonResponse.surveys) {
          const surveys = Object.values(jsonResponse.surveys).filter((survey) => survey.identifier);
          if (surveys.length > 1) {
            surveys.sort((a, b) => b.version - a.version);
            dispatch(propertyChange(SURVEYS_COMPARE, surveys));
            navigate.to(SURVEYS_COMPARE_PATH, { state: true });
            dispatch(endLoading());
          } else {
            dispatch(getVersionForComparison(identifier, lastVersion));
          }
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

export function getVersionForComparison(identifier, currentVersion, lastVersion) {
  return (dispatch, getState) => {
    rest.doGet(`/admin/admin/surveys/${identifier}/version/${currentVersion}`).then(
      (json) => {
        if (json.identifier) {
          if (lastVersion) {
            dispatch(propertyChange(SURVEY_COMPARE_LATEST, json));
            dispatch(loadSuccessful(json, json.links));
            // get the diff versions
            dispatch(getVersionsForComparison(identifier, lastVersion));
          } else {
            dispatch(propertyChange(SURVEY_COMPARE_OLD, json));
            navigate.to(SURVEYS_COMPARE_PATH, { state: true });
            dispatch(endLoading());
          }
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

/**
 * Save successful action
 */
export function saveSuccessful(template, links) {
  return loadSuccessful(template, links, false);
}

/**
 * Load successful action
 */
export function loadSuccessful(json, links, showPreview, resetPage = true) {
  return {
    [TYPE_KEY]: LOAD_SUCCESSFUL_ACTION,
    [LINKS_KEY]: links,
    [SURVEY_IDENTIFIER]: json[SURVEY_IDENTIFIER],
    [SURVEY_VERSION]: json[SURVEY_VERSION],
    [SURVEY_NAME]: json[SURVEY_NAME],
    [QUESTION_SYNC]: json[QUESTION_SYNC],
    [SURVEY_FUNDING_AREA]: json[$survey.fundingArea],
    [SURVEY_PROGRAM]: json[$survey.program],
    [SURVEY_PROGRAM_INSTANCE]: json[$survey.programInstance],
    [SURVEY_PROJECT]: json[$survey.project],
    [SURVEY_LANGUAGE]: json[$survey.language],
    [SURVEY_DESCRIPTION]: json[$survey.description],
    [SURVEY_TEMPLATE]: json[SURVEY_TEMPLATE],
    [SURVEY_RESET_PAGE]: resetPage,
    [SHOW_SURVEY_PREVIEW]: showPreview
  };
}

/**
 * Load successful action
 */
export function loadSuccessfulAndCreatePDF(json) {
  return async (dispatch, getState) => {
    let surveyResult = getState().surveyView.surveyResult;
    let userName = getState().login.user;
    const employeeNumber = await loadEmployeeNumberForEditor(surveyResult);
    dispatch({
      [TYPE_KEY]: LOAD_SUCCESSFUL_AND_CREATE_PDF_ACTION,
      [SURVEY_IDENTIFIER]: json[SURVEY_IDENTIFIER],
      [SURVEY_VERSION]: json[SURVEY_VERSION],
      [SURVEY_NAME]: json[SURVEY_NAME],
      [SURVEY_FUNDING_AREA]: json[$survey.fundingArea],
      [SURVEY_PROGRAM]: json[$survey.program],
      [SURVEY_PROGRAM_INSTANCE]: json[$survey.programInstance],
      [SURVEY_PROJECT]: json[$survey.project],
      [SURVEY_LANGUAGE]: json[$survey.language],
      [SURVEY_DESCRIPTION]: json[$survey.description],
      [SURVEY_TEMPLATE]: json[SURVEY_TEMPLATE],
      [SURVEY_RESULT]: surveyResult,
      [SURVEY_BUFFER]: false,
      [USER]: userName,
      [EMPLOYEE_NUMBER]: employeeNumber
    });
  };
}

export function loadSuccessfulCreateBuffer(json, pdfCallBack) {
  return async (dispatch, getState) => {
    let surveyResult = getState().surveyView.surveyResult;
    let userName = getState().login.user;
    const employeeNumber = await loadEmployeeNumberForEditor(surveyResult);
    dispatch({
      [TYPE_KEY]: LOAD_SUCCESSFUL_AND_CREATE_PDF_BUFFER,
      [SURVEY_IDENTIFIER]: json[SURVEY_IDENTIFIER],
      [SURVEY_VERSION]: json[SURVEY_VERSION],
      [SURVEY_NAME]: json[SURVEY_NAME],
      [SURVEY_FUNDING_AREA]: json[$survey.fundingArea],
      [SURVEY_PROGRAM]: json[$survey.program],
      [SURVEY_PROGRAM_INSTANCE]: json[$survey.programInstance],
      [SURVEY_PROJECT]: json[$survey.project],
      [SURVEY_LANGUAGE]: json[$survey.language],
      [SURVEY_DESCRIPTION]: json[$survey.description],
      [SURVEY_TEMPLATE]: json[SURVEY_TEMPLATE],
      [SURVEY_RESULT]: surveyResult,
      [SURVEY_BUFFER]: true,
      [PDF_CALL_BACK]: pdfCallBack,
      [USER]: userName,
      [EMPLOYEE_NUMBER]: employeeNumber
    });
  };
}

async function loadEmployeeNumberForEditor(surveyResult) {
  const username = surveyResult?.lastEditedBy;
  if (!username) {
    return "";
  }

  try {
    const user = await rest.doGet(`/admin/users?username=${username}`);
    return user?.employeeNumber || "";
  } catch (error) {
    return "";
  }
}

export function createSurvey(
  surveyName,
  fundingArea,
  program,
  programInstance,
  project,
  surveyLanguage,
  surveyTemplate
) {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: CREATE_NEW_ACTION,
      [SURVEY_IDENTIFIER]: null,
      [SURVEY_NAME]: surveyName,
      [SURVEY_FUNDING_AREA]: fundingArea,
      [SURVEY_PROGRAM]: program,
      [SURVEY_PROGRAM_INSTANCE]: programInstance,
      [SURVEY_PROJECT]: project,
      [SURVEY_LANGUAGE]: surveyLanguage,
      [SURVEY_TEMPLATE]: coalesce(surveyTemplate, { pages: [] }),
      [SURVEY_DESCRIPTION]: null,
      [EDIT_SURVEY_HEADER]: false
    });
    if (!surveyTemplate) {
      dispatch(resetPages());
    }
  };
}

export function resetSurvey() {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: RESET_SURVEY_ACTION
    });
  };
}

export function previewSurvey() {
  return (dispatch) => {
    dispatch(resetSurveyView());
    dispatch({
      [TYPE_KEY]: PREVIEW_SURVEY_SHOW_HIDE_ACTION,
      [SHOW_SURVEY_PREVIEW]: true
    });
  };
}

export function hidePreviewSurvey() {
  return (dispatch) => {
    dispatch(resetSurveyView());
    dispatch({
      [TYPE_KEY]: PREVIEW_SURVEY_SHOW_HIDE_ACTION,
      [SHOW_SURVEY_PREVIEW]: false
    });
  };
}

export function showQuestionSyncDialog() {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: QUESTION_SYNC_SHOW_HIDE_ACTION,
      [SYNC_DIALOG_OPEN]: true
    });
  };
}

export function hideQuestionSyncDialog() {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: QUESTION_SYNC_SHOW_HIDE_ACTION,
      [SYNC_DIALOG_OPEN]: false
    });
  };
}

export const syncQuestions = (template) => {
  return (dispatch) => {
    rest
      .doGet(
        //survey._links.self[1].href //it didn't work because the deploy was bad.
        `/admin/sync/questions/${template.identifier}/version/${template.version}`
      )
      .then(
        (json) => {
          if (json.isSynched) {
            dispatch(hideQuestionSyncDialog());
            dispatch(messagesReceived(createSuccessMessages("survey.editor.message.template_synced")));
          } else {
            dispatch(messagesReceived(createErrorMessages(json)));
          }
        },
        (reason) => {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
        }
      );
  };
};

/**
 * Change Edit Mode
 */
export function changeEditState(edit) {
  return {
    [TYPE_KEY]: CHANGE_EDIT_STATE_ACTION,
    [EDIT_SURVEY_HEADER]: edit
  };
}

/**
 * Change Title and Description of Survey
 */
export function onConfirm() {
  return {
    [TYPE_KEY]: EDIT_HEADER_CONFIRMED_ACTION
  };
}

export function refreshTemplateVersions(identifier) {
  return async (dispatch) => {
    const [history, currentTemplate] = await Promise.all([
      rest.doGet("/admin/admin/surveys/history/" + identifier),
      rest.doGet("/admin/admin/surveys/" + identifier)
    ]);
    dispatch(loadHistorySuccessful(identifier, history.historyList));
    dispatch(templateUpdated(currentTemplate));
  };
}
